// Composer toolbar pill that mirrors WorkflowDestinationButton's style
// for visual coherence. Clicking opens the FoldersFilePicker — an
// inline modal that lets the user pull a doc directly from one of
// their projects without leaving chat.
//
// File pull flow:
//   1. Click pill → picker opens
//   2. Pick a file → fetch /api/file/:fileId → blob → File
//   3. onPickFile(file) → parent attaches via existing handleFilesReceived
//
// Self-contained picker state so callers only need onPickFile + getToken.
import { useState, type CSSProperties } from 'react';
import { APP } from './tokens';
import { FoldersFilePicker } from './FoldersFilePicker';
import { AppFolderIcon } from './AppFolderIcon';

export interface FoldersButtonProps {
  /** Bearer-token getter for the file fetch inside the picker. */
  getToken: () => string | null;
  /** Called once the user picks a file and we've fetched it as a
   *  browser File. Parent attaches it to the composer's currentFiles. */
  onPickFile: (file: File) => void;
  /** Hide entirely (e.g. for unauthenticated users). */
  enabled?: boolean;
}

export function FoldersButton({ getToken, onPickFile, enabled = true }: FoldersButtonProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!enabled) return null;
  return (
    <>
      <button type="button" onClick={() => setPickerOpen(true)} style={PILL}>
        <AppFolderIcon width={16} height={13} />
        <span>Projects</span>
      </button>
      <FoldersFilePicker
        open={pickerOpen}
        getToken={getToken}
        onPickFile={onPickFile}
        onClose={() => setPickerOpen(false)}
      />
    </>
  );
}

// Mirrors WorkflowDestinationButton's PILL style so the two read as a
// matched pair in the toolbar.
const PILL: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  borderRadius: 8,
  border: 'none',
  background: 'transparent',
  color: APP.textPrimary,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
};
